<template>
  <div class="delivery-order-section">
    <p class="delivery-order-section__title">
      {{ title }}
    </p>
    <div class="delivery-order-section__card">
      <McTable
        class="delivery-order-section__delivery-table"
        :fields="deliveryOrderFields"
        :apiClient="apiClient"
        searchable
        :has-delete="false"
        :has-checkbox="false"
        :sticky-header="true"
        :isFirstRowClickable="false"
        :with-card="false"
      >
        <template #[`cell(status.name)`]="{ value, item }">
          <span
            v-if="item.status?.color"
            class="__status-dots"
            :style="{ backgroundColor: item.status.color }"
          ></span>
          <span class="mc-text-status">{{ value }}</span>
        </template>
      </McTable>
    </div>
  </div>
</template>

<script>
import { formatIndexDate } from '@/helpers/tzDate'

import * as DeliveryOrder from '@/services/rest-api/lib/delivery/delivery-order'
import McTable from '@/components/McTable'

export default {
  components: {
    McTable,
  },
  props: {
    title: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    deliveryOrderId: {
      type: String,
      default: null,
    },
    deliveryOrderIds: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      deliveryOrderFields: [
        {
          key: 'number',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.deliveryNumber'),
          isSortable: true,
        },
        {
          key: 'status.name',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.deliveryStatus'),
          isSortable: false,
        },
        {
          key: 'shipment_number',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.shipmentNumber'),
          isSortable: false,
          formatter: (value) => value || '-',
        },
        {
          key: 'reference_number',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.referenceNumber'),
          isSortable: false,
          formatter: (value) => value || '-',
        },
        {
          key: 'shipped_customer.name',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.customer'),
          isSortable: false,
        },
        {
          key: 'expected_pickup_on',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.targetPickedUp'),
          isSortable: false,
          formatter: (value) => {
            const datetime = value ? formatIndexDate(value) : '-'

            return datetime?.date && datetime?.time
              ? `${datetime.date}, ${datetime.time}`
              : datetime
          },
        },
        {
          key: 'expected_delivered_on',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.targetDelivered'),
          isSortable: false,
          formatter: (value) => {
            const datetime = value ? formatIndexDate(value) : '-'

            return datetime?.date && datetime?.time
              ? `${datetime.date}, ${datetime.time}`
              : datetime
          },
        },
        {
          key: 'timestamp.picked_on',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.actualPickedUp'),
          isSortable: false,
          formatter: (value) => {
            const datetime = value ? formatIndexDate(value) : '-'

            return datetime?.date && datetime?.time
              ? `${datetime.date}, ${datetime.time}`
              : datetime
          },
        },
        {
          key: 'load_min_temperature',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.minTemperature'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} °C`),
        },
        {
          key: 'load_max_temperature',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.maxTemperature'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} °C`),
        },
        {
          key: 'current_load_weight',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.weight'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} Kg`),
        },
        {
          key: 'current_load_volume',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.volume'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} Cbm`),
        },
      ],
    }
  },
  computed: {
    apiClient() {
      return {
        get: async (opts) => {
          const queryParamFilters = [`id in ${this.deliveryOrderIdsFormatted}`]

          const getDeliveryOrder = DeliveryOrder.get(queryParamFilters)
          const res = await getDeliveryOrder(opts)

          return res
        },
      }
    },
    deliveryOrderIdsFormatted() {
      return this.deliveryOrderId !== null
        ? this.deliveryOrderId
        : Object.keys(this.deliveryOrderIds)
            .filter((key) => this.deliveryOrderIds[key] === true)
            .join(' ')
    },
  },
}
</script>

<style lang="scss" scoped>
.delivery-order-section {
  &__title {
    font-size: 14px;
  }

  &__card {
    border: 1px solid $mep-color-text-100;
    border-radius: 4px;
    padding: 12px;

    :deep(.b-table) {
      .__busy {
        min-height: fit-content;
        display: grid;
        place-items: center;
        place-content: center;
      }

      th {
        position: sticky;
        top: 0;

        &:has(.sort-icon-up),
        &:has(.sort-icon-down) {
          padding-right: 30px;
        }

        .sort-icon-up {
          position: absolute;
          right: 10px;
          top: 8px;
          opacity: 0.7;
        }
        .sort-icon-down {
          position: absolute;
          right: 10px;
          bottom: 8px;
          opacity: 0.7;
        }
      }
    }
  }

  &__info {
    width: fit-content;

    &-title,
    &-value {
      margin-bottom: 4px;
    }

    &-title {
      color: $mep-color-text-300;
    }

    &-value {
      color: $mep-color-text-400;
    }

    &-status {
      width: fit-content;
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 11px;
    }
  }

  &__loads-table {
    border: 1px solid $mep-color-text-100;
    border-radius: 4px;
    margin-top: 16px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.__status-dots {
  display: inline-block;
  margin: auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
}
</style>
